import { useState, useEffect, useCallback } from 'react';
import {
    bannerService,
    TelaBanner,
    TelaBannerOtimizada,
    TelaBannerBasica,
    TelaBannerParaTelevisao,
    TelaBannerParaVideo,
    InserirTelaBannerCommand,
    AlterarTelaBannerCommand
} from '../services/bannerService';

export const useBanners = () => {
    const [telas, setTelas] = useState<TelaBanner[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);

    const carregarTelas = useCallback(async (id?: number) => {
        setLoading(true);
        setError(null);

        try {
            const result = await bannerService.listarTelas(id);
            setTelas(result);
            setTotalCount(result.length);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar telas';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, []);

    const criarTela = async (command: InserirTelaBannerCommand) => {
        setLoading(true);
        setError(null);

        try {
            const novaTela = await bannerService.criarTela(command);
            // Recarregar a lista após criar
            await carregarTelas();
            return novaTela;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao criar tela';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const atualizarTela = async (command: AlterarTelaBannerCommand) => {
        setLoading(true);
        setError(null);

        try {
            const sucesso = await bannerService.atualizarTela(command);
            if (sucesso) {
                // Recarregar a lista após atualizar
                await carregarTelas();
            }
            return sucesso;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar tela';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deletarTela = async (telaId: number) => {
        setLoading(true);
        setError(null);

        try {
            // Primeiro, remover a tela de todas as televisões onde ela está cadastrada
            try {
                const { televisaoService } = await import('../../televisao/services/televisaoService');
                const televisoesAtualizadas = await televisaoService.removerTelaDeTodasTelevisoes(telaId);
                if (televisoesAtualizadas > 0) {
                    console.log(`✅ [useBanners] Tela ${telaId} removida de ${televisoesAtualizadas} televisão(ões)`);
                }
            } catch (error) {
                console.error(`⚠️ [useBanners] Erro ao remover tela das televisões (continuando com a deleção):`, error);
                // Continuar com a deleção mesmo se houver erro ao remover das televisões
            }

            // Depois, deletar a tela (panfleto: foto antes, via bannerService)
            const tela = telas.find(t => t.id === telaId);
            const sucesso = await bannerService.deletarTela(telaId, tela?.tipo);
            if (sucesso) {
                setTelas(prev => prev.filter(t => t.id !== telaId));
                setTotalCount(prev => prev - 1);
            }
            return sucesso;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao deletar tela';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const uploadVideo = async (telaId: number, videoFile: File) => {
        setLoading(true);
        setError(null);

        try {
            await bannerService.uploadVideo(telaId, videoFile);
            await carregarTelas(); // Recarregar telas após upload
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao enviar vídeo';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const downloadVideo = async (telaId: number) => {
        setLoading(true);
        setError(null);

        try {
            const blob = await bannerService.downloadVideo(telaId);
            return blob;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao baixar vídeo';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const getVideoUrl = (telaId: number): string => {
        return bannerService.getVideoUrl(telaId);
    };

    useEffect(() => {
        carregarTelas();
    }, [carregarTelas]);

    return {
        telas,
        loading,
        error,
        totalCount,
        carregarTelas,
        criarTela,
        atualizarTela,
        deletarTela,
        uploadVideo,
        downloadVideo,
        getVideoUrl,
    };
};

/**
 * Hook apenas para operações de escrita (criar, atualizar, deletar)
 * NÃO carrega telas automaticamente - use quando só precisar das operações de escrita
 * Isso evita chamadas duplicadas à API quando usado junto com useBannersOtimizado
 */
export const useBannersWrite = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const criarTela = async (command: InserirTelaBannerCommand) => {
        setLoading(true);
        setError(null);

        try {
            const novaTela = await bannerService.criarTela(command);
            return novaTela;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao criar tela';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const atualizarTela = async (command: AlterarTelaBannerCommand) => {
        setLoading(true);
        setError(null);

        try {
            const sucesso = await bannerService.atualizarTela(command);
            return sucesso;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar tela';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deletarTela = async (telaId: number, tipo?: number) => {
        setLoading(true);
        setError(null);

        try {
            // Primeiro, remover a tela de todas as televisões onde ela está cadastrada
            try {
                const { televisaoService } = await import('../../televisao/services/televisaoService');
                const televisoesAtualizadas = await televisaoService.removerTelaDeTodasTelevisoes(telaId);
                if (televisoesAtualizadas > 0) {
                    console.log(`✅ [useBannersWrite] Tela ${telaId} removida de ${televisoesAtualizadas} televisão(ões)`);
                }
            } catch (error) {
                console.error(`⚠️ [useBannersWrite] Erro ao remover tela das televisões (continuando com a deleção):`, error);
                // Continuar com a deleção mesmo se houver erro ao remover das televisões
            }

            // Depois, deletar a tela (panfleto tipo 1: foto antes, via bannerService)
            const sucesso = await bannerService.deletarTela(telaId, tipo);
            return sucesso;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao deletar tela';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        error,
        criarTela,
        atualizarTela,
        deletarTela,
    };
};

/**
 * Hook otimizado para buscar telas - retorna apenas os campos necessários
 * para exibição na lista, reduzindo significativamente o tamanho da resposta
 * 
 * Otimizações:
 * - Usa useCallback para evitar recriação da função
 * - Retorna apenas campos essenciais (id, nome, descricao, tipo, ativo, produtoIds como números)
 * - Extrai apenas IDs dos produtos (não objetos completos), reduzindo drasticamente o tamanho da resposta
 * - Permite controlar se deve carregar automaticamente ou manualmente
 * 
 * @param autoLoad - Se true, carrega automaticamente ao montar. Se false, só carrega quando chamar carregarTelas() manualmente
 * 
 * DIFERENÇA ENTRE useBanners() e useBannersOtimizado():
 * - useBanners(): Retorna TelaBanner[] completo com todos os campos e objetos ProdutoBanner completos
 * - useBannersOtimizado(): Retorna TelaBannerOtimizada[] apenas com campos essenciais e produtoIds como números
 * 
 * QUANDO USAR CADA UM:
 * - useBannersOtimizado(): Para listagens, dropdowns, verificações rápidas (99% dos casos)
 * - useBanners(): Apenas quando precisar de TODOS os dados completos dos produtos (raro)
 */
export const useBannersOtimizado = (autoLoad: boolean = true) => {
    const [telas, setTelas] = useState<TelaBannerOtimizada[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);

    const carregarTelas = useCallback(async (id?: number) => {
        setLoading(true);
        setError(null);

        try {
            const result = await bannerService.listarTelasOtimizado(id);
            setTelas(result);
            setTotalCount(result.length);
        } catch (err: any) {
            // Tratar erros de abort de forma mais específica
            if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
                // Se foi abortado, não tratar como erro fatal - pode ser timeout ou cancelamento
                // Não definir erro para não mostrar mensagem de erro ao usuário
                // Apenas não atualizar os dados
            } else {
                const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar telas';
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // Carregar apenas se autoLoad for true
    useEffect(() => {
        let isMounted = true;
        
        if (autoLoad) {
            carregarTelas().catch(() => {
                // Erro silencioso - não fazer nada se o componente ainda estiver montado
            });
        }
        
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoad]);

    // Função para atualizar uma tela específica no cache sem fazer nova busca
    const atualizarTelaNoCache = useCallback((telaId: number, atualizacoes: Partial<TelaBannerOtimizada>) => {
        setTelas(prev => prev.map(tela => 
            tela.id === telaId 
                ? { ...tela, ...atualizacoes }
                : tela
        ));
    }, []);

    // Função para adicionar uma nova tela no cache sem fazer nova busca
    const adicionarTelaNoCache = useCallback((novaTela: TelaBannerOtimizada) => {
        setTelas(prev => [...prev, novaTela]);
        setTotalCount(prev => prev + 1);
    }, []);

    return {
        telas,
        loading,
        error,
        totalCount,
        carregarTelas,
        atualizarTelaNoCache,
        adicionarTelaNoCache,
    };
};

/**
 * Hook ULTRA-OTIMIZADO para listagem básica de telas (DisplayPage)
 * Retorna APENAS: id, nome, descricao, tipo
 * 
 * NÃO carrega: configuracoes (layoutStyle), produtos, imagens, vídeos
 * 
 * O estilo só precisa ser carregado quando visualizar uma tela específica.
 * Para o loading inicial, só precisamos das informações dos cards.
 * 
 * Isso reduz drasticamente o tamanho da resposta da API (sem imagens base64, sem layoutStyle completo).
 * 
 * @param autoLoad - Se true, carrega automaticamente ao montar. Se false, só carrega quando chamar carregarTelas() manualmente
 */
export const useBannersBasico = (autoLoad: boolean = true) => {
    const [telas, setTelas] = useState<TelaBannerBasica[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);

    const carregarTelas = useCallback(async (id?: number) => {
        setLoading(true);
        setError(null);

        try {
            const result = await bannerService.listarTelasBasico(id);
            setTelas(result);
            setTotalCount(result.length);
        } catch (err: any) {
            if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
                // Se foi abortado, não tratar como erro fatal
            } else {
                const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar telas';
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // Carregar apenas se autoLoad for true
    useEffect(() => {
        let isMounted = true;
        
        if (autoLoad) {
            carregarTelas().catch(() => {
                // Erro silencioso
            });
        }
        
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoad]);

    return {
        telas,
        loading,
        error,
        totalCount,
        carregarTelas,
    };
};

/**
 * Hook OTIMIZADO para listagem de telas na televisão
 * Retorna JSON bruto de configuracoes mas SEM processar (não baixa imagens/vídeos)
 * 
 * Para televisão, não precisamos interpretar vídeos e imagens.
 * Apenas carregamos o JSON mas não baixamos nem processamos nada.
 * Só será inserido telas na televisão, não será customizado nem editado.
 * 
 * @param autoLoad - Se true, carrega automaticamente ao montar. Se false, só carrega quando chamar carregarTelas() manualmente
 */
export const useBannersParaTelevisao = (autoLoad: boolean = true) => {
    const [telas, setTelas] = useState<TelaBannerParaTelevisao[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);

    const carregarTelas = useCallback(async (id?: number) => {
        setLoading(true);
        setError(null);

        try {
            const result = await bannerService.listarTelasParaTelevisaoBasico(id);
            setTelas(result);
            setTotalCount(result.length);
        } catch (err: any) {
            if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
                // Se foi abortado, não tratar como erro fatal
            } else {
                const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar telas';
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // Carregar apenas se autoLoad for true
    useEffect(() => {
        let isMounted = true;
        
        if (autoLoad) {
            carregarTelas().catch(() => {
                // Erro silencioso
            });
        }
        
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoad]);

    return {
        telas,
        loading,
        error,
        totalCount,
        carregarTelas,
    };
};

/**
 * Hook para página de Style Editor
 * Usa `listarTelasOtimizado` (GET /v1/banner/tela): inclui `produtoIds` para saber se a tela tem produto
 * e `configuracoes` para o layout — um único fluxo alinhado ao display.
 *
 * @param autoLoad - Se true, carrega automaticamente ao montar. Se false, só carrega quando chamar carregarTelas() manualmente
 */
export const useBannersParaStyleEditor = (autoLoad: boolean = true) => {
    const [telas, setTelas] = useState<TelaBannerOtimizada[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);

    const carregarTelas = useCallback(async (id?: number) => {
        setLoading(true);
        setError(null);

        try {
            const result = await bannerService.listarTelasOtimizado(id);
            setTelas(result);
            setTotalCount(result.length);
        } catch (err: any) {
            // Tratar erros de abort de forma mais específica
            if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
                // Se foi abortado, não tratar como erro fatal
            } else {
                const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar telas';
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // Carregar apenas se autoLoad for true
    useEffect(() => {
        let isMounted = true;
        
        if (autoLoad) {
            carregarTelas().catch(() => {
                // Erro silencioso
            });
        }
        
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoad]);

    return {
        telas,
        loading,
        error,
        totalCount,
        carregarTelas,
    };
};

/**
 * Hook otimizado para página de Vídeos
 * Retorna: id, nome, tipo, ativo, video (flag indicando se tem vídeo)
 * 
 * Usa endpoint otimizado: produtos=N
 * - Não retorna produtos (não precisa para listagem)
 * - Retorna flag `video` (true/false) para saber se a tela tem vídeo
 * 
 * Quando uma tela for selecionada e tiver `video === true`, carregue o vídeo atual.
 * Se `video === false` ou `undefined`, a pessoa pode fazer upload de um novo vídeo.
 * 
 * @param autoLoad - Se true, carrega automaticamente ao montar. Se false, só carrega quando chamar carregarTelas() manualmente
 */
export const useBannersParaVideo = (autoLoad: boolean = true) => {
    const [telas, setTelas] = useState<TelaBannerParaVideo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);

    const carregarTelas = useCallback(async (id?: number) => {
        setLoading(true);
        setError(null);

        try {
            const result = await bannerService.listarTelasOtimizadoParaVideo(id);
            setTelas(result);
            setTotalCount(result.length);
        } catch (err: any) {
            // Tratar erros de abort de forma mais específica
            if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
                // Se foi abortado, não tratar como erro fatal
            } else {
                const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar telas';
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // Carregar apenas se autoLoad for true
    useEffect(() => {
        let isMounted = true;
        
        if (autoLoad) {
            carregarTelas().catch(() => {
                // Erro silencioso
            });
        }
        
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoad]);

    return {
        telas,
        loading,
        error,
        totalCount,
        carregarTelas,
    };
};
